import * as Dialog from "@radix-ui/react-dialog"
import { ChevronDown, Loader2, Settings2, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Badge, Button } from "@/components/ui/primitives"
import { PostProductionIcon } from "@/components/icons/post-production-icon"
import { ParametersSchemaForm } from "@/components/shot-panel/parameters-schema-form"
import { confirm } from "@/lib/confirm"
import {
  createPostProductionVersion,
  fetchPostProductionModels,
  type PostProductionModelOption,
  type PostProductionParameters,
  type PostProductionSourceResolution,
} from "@/lib/workspace-api"
import { cn } from "@/lib/utils"
import {
  firstPostProductionParameterErrorKey,
  defaultsFromPostProductionSchema,
  parsePostProductionParameterServerError,
  scrollToPostProductionParameterField,
  validatePostProductionParameters,
  type PostProductionParameterErrors,
} from "@/lib/post-production-parameters"
import { useTranslation } from "@/hooks/use-translation"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  shotId: number
  sourceVersionId: number | null
  onSubmitted?: () => void
}

function defaultVersionId(model: PostProductionModelOption | null) {
  if (!model) return null
  return (model.versions.find((version) => version.default_version) ?? model.versions[0])?.id ?? null
}

function providerLabel(provider: PostProductionModelOption["provider"]) {
  switch (provider) {
    case "topaz":
      return "Topaz"
    default:
      return provider
  }
}

const versionSelectClass =
  "h-8 w-full appearance-none rounded-lg border border-ui-border bg-ui-surface bg-none px-2 pr-7 text-xs text-ui-text-primary focus:ring-2 focus:ring-[var(--ui-focus-ring)] focus:outline-none [-webkit-appearance:none]"

// Длинная сторона больше 4K UHD — предупреждаем о большом файле перед запуском.
const LARGE_OUTPUT_LONG_SIDE = 3840

function predictedOutputResolution(
  source: PostProductionSourceResolution | null,
  model: PostProductionModelOption | null,
  parameters: PostProductionParameters,
): PostProductionSourceResolution | null {
  if (!source || !model) return null
  const raw = parameters["upscale_factor"] ?? model.parameters_schema.properties?.["upscale_factor"]?.default
  const factor = Number(raw)
  if (!Number.isFinite(factor) || factor <= 1) return null
  return { width: Math.round(source.width * factor), height: Math.round(source.height * factor) }
}

export function PostProductionLaunchDialog({
  open,
  onOpenChange,
  projectId,
  shotId,
  sourceVersionId,
  onSubmitted,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const { t: tShot } = useTranslation("shot")
  const [models, setModels] = useState<PostProductionModelOption[]>([])
  const [sourceResolution, setSourceResolution] = useState<PostProductionSourceResolution | null>(null)
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null)
  const [selectedVersionByModel, setSelectedVersionByModel] = useState<Record<number, number>>({})
  const [parameters, setParameters] = useState<PostProductionParameters>({})
  const [parametersOpen, setParametersOpen] = useState(false)
  const [parameterErrors, setParameterErrors] = useState<PostProductionParameterErrors>({})
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    void fetchPostProductionModels(projectId, shotId)
      .then(({ models: items, source_resolution }) => {
        if (cancelled) return
        setModels(items)
        setSourceResolution(source_resolution)
        const first = items.find((model) => !model.platform_disabled) ?? null
        setSelectedModelId((current) => {
          if (current != null && items.some((model) => model.id === current && !model.platform_disabled)) return current
          return first?.id ?? null
        })
        const defaults = items.reduce<Record<number, number>>((acc, model) => {
          const id = defaultVersionId(model)
          if (id != null) acc[model.id] = id
          return acc
        }, {})
        setSelectedVersionByModel((current) => ({ ...defaults, ...current }))
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, shotId])

  useEffect(() => {
    if (!open) return
    setParametersOpen(false)
    setParameterErrors({})
    setLoadError(null)
    setSubmitError(null)
  }, [open, sourceVersionId])

  const selectedModel = useMemo(
    () => models.find((model) => model.id === selectedModelId) ?? null,
    [models, selectedModelId],
  )
  const selectedVersionId =
    selectedModelId != null ? (selectedVersionByModel[selectedModelId] ?? defaultVersionId(selectedModel)) : null
  const selectedVersion = selectedModel?.versions.find((version) => version.id === selectedVersionId) ?? null

  useEffect(() => {
    if (!open || !selectedModel) return
    setParameters(defaultsFromPostProductionSchema(selectedModel.parameters_schema))
  }, [open, sourceVersionId, selectedModel])

  const handleSelectModel = (model: PostProductionModelOption) => {
    if (model.platform_disabled) return
    if (model.id === selectedModelId) return
    setSelectedModelId(model.id)
    setParameters(defaultsFromPostProductionSchema(model.parameters_schema))
    setParameterErrors({})
    setSubmitError(null)
    setParametersOpen(false)
  }

  const handleParametersChange = (next: PostProductionParameters) => {
    setParameters(next)
    setSubmitError(null)
    setParameterErrors((current) => {
      if (Object.keys(current).length === 0) return current
      const updated = { ...current }
      for (const key of Object.keys(updated)) {
        if (parameters[key] !== next[key]) {
          delete updated[key]
        }
      }
      return updated
    })
  }

  const showParameterValidationErrors = (errors: PostProductionParameterErrors) => {
    setParameterErrors(errors)
    setParametersOpen(true)
    const firstKey = selectedModel
      ? firstPostProductionParameterErrorKey(selectedModel.parameters_schema, errors)
      : undefined
    if (firstKey) {
      window.setTimeout(() => scrollToPostProductionParameterField(firstKey), 50)
    }
  }

  const predictedOutput = predictedOutputResolution(sourceResolution, selectedModel, parameters)
  const isLargeOutput =
    predictedOutput != null && Math.max(predictedOutput.width, predictedOutput.height) > LARGE_OUTPUT_LONG_SIDE

  const handleSubmit = async () => {
    if (!sourceVersionId || !selectedVersionId || !selectedModel || selectedModel.platform_disabled) return

    const validationErrors = validatePostProductionParameters(selectedModel.parameters_schema, parameters, tShot)
    if (Object.keys(validationErrors).length > 0) {
      showParameterValidationErrors(validationErrors)
      return
    }

    if (isLargeOutput) {
      if (
        !(await confirm({
          title: t("postProduction.largeOutputConfirm.title"),
          message: t("postProduction.largeOutputConfirm.message", {
            width: predictedOutput?.width ?? 0,
            height: predictedOutput?.height ?? 0,
          }),
          confirmLabel: t("postProduction.largeOutputConfirm.confirm"),
        }))
      ) {
        return
      }
    }

    await submitVersion()
  }

  const submitVersion = async () => {
    if (!sourceVersionId || !selectedVersionId || !selectedModel) return

    setSubmitting(true)
    setSubmitError(null)
    setParameterErrors({})
    try {
      await createPostProductionVersion(projectId, shotId, {
        source_version_id: sourceVersionId,
        generation_model_id: selectedVersionId,
        parameters,
      })
      toast.success(t("postProduction.queued"))
      onOpenChange(false)
      onSubmitted?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      const parsed = parsePostProductionParameterServerError(message)
      if (parsed.field && selectedModel) {
        const revalidated = validatePostProductionParameters(selectedModel.parameters_schema, parameters, tShot)
        showParameterValidationErrors({
          [parsed.field]: revalidated[parsed.field] ?? parsed.message,
        })
      } else {
        setSubmitError(message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[111] flex max-h-[min(92vh,820px)] w-[min(96vw,46rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated">
          <div className="flex shrink-0 items-start justify-between border-b border-ui-border p-5 pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-ui-text-primary">
                {t("postProduction.launchTitle")}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-ui-text-muted">
                {t("postProduction.launchDescription")}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto p-5">
            {!sourceVersionId ? (
              <p className="text-sm text-ui-status-rendering">{t("postProduction.noSource")}</p>
            ) : loading ? (
              <p className="flex items-center gap-2 text-sm text-ui-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("postProduction.loadingModels")}
              </p>
            ) : loadError ? (
              <p className="text-sm text-ui-status-failed">{loadError}</p>
            ) : models.length === 0 ? (
              <p className="text-sm text-ui-text-muted">{t("postProduction.noModels")}</p>
            ) : (
              <div className="flex flex-col gap-3">
                {models.map((model) => {
                  const platformDisabled = model.platform_disabled === true
                  const active = !platformDisabled && model.id === selectedModelId
                  const versionId = selectedVersionByModel[model.id] ?? defaultVersionId(model)
                  const hasManyVersions = model.versions.length > 1
                  const description = model.description.trim()
                  const card = (
                    <div
                      role="button"
                      tabIndex={platformDisabled ? -1 : 0}
                      aria-disabled={platformDisabled || undefined}
                      onClick={() => handleSelectModel(model)}
                      onKeyDown={(event) => {
                        if (platformDisabled) return
                        if (event.target !== event.currentTarget) return
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault()
                          handleSelectModel(model)
                        }
                      }}
                      className={cn(
                        "rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
                        platformDisabled
                          ? "cursor-not-allowed border-ui-border bg-ui-surface-sunken opacity-40"
                          : active
                            ? "cursor-pointer border-ui-accent/70 bg-ui-accent/10"
                            : "cursor-pointer border-ui-border bg-ui-surface-sunken hover:border-ui-border-strong",
                      )}
                    >
                      <div className="relative w-full pr-5 text-left">
                        <div className="min-w-0">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-semibold text-ui-text-primary">{model.name}</span>
                            <Badge tone="neutral" className="shrink-0 px-2 py-0 text-[10px]">
                              {providerLabel(model.provider)}
                            </Badge>
                          </div>
                          {description ? (
                            <p className="mt-1 text-xs leading-relaxed text-ui-text-secondary">{description}</p>
                          ) : null}
                        </div>
                        <PostProductionIcon
                          className={cn(
                            "pointer-events-none absolute top-0 right-0 h-4 w-4 shrink-0 text-ui-accent",
                            !active && "invisible",
                          )}
                        />
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
                          {t("postProduction.version")}
                        </span>
                        <span className="relative min-w-0 flex-1">
                          <select
                            value={versionId ?? ""}
                            disabled={platformDisabled}
                            onChange={(event) => {
                              setSelectedVersionByModel((current) => ({
                                ...current,
                                [model.id]: Number(event.target.value),
                              }))
                              if (!active) handleSelectModel(model)
                            }}
                            className={cn(versionSelectClass, !hasManyVersions && "text-ui-text-secondary opacity-80")}
                            style={{ WebkitAppearance: "none", appearance: "none", backgroundImage: "none" }}
                          >
                            {model.versions.length > 0 ? (
                              model.versions.map((version) => (
                                <option key={version.id} value={version.id}>
                                  {version.name}
                                </option>
                              ))
                            ) : (
                              <option value="">{t("postProduction.noVersion")}</option>
                            )}
                          </select>
                          <ChevronDown className="pointer-events-none absolute top-2 right-2 h-3.5 w-3.5 text-ui-text-muted" />
                        </span>
                      </div>
                    </div>
                  )

                  return (
                    <ActionHintTooltip
                      key={model.id}
                      label={platformDisabled ? t("common:platformDisabledModel") : undefined}
                      side="top"
                      className="block w-full"
                    >
                      {card}
                    </ActionHintTooltip>
                  )
                })}
              </div>
            )}
          </div>

          {sourceVersionId && !loading && !loadError && selectedModel ? (
            <div className="shrink-0 border-t border-ui-border bg-ui-surface-sunken/60">
              <button
                type="button"
                onClick={() => setParametersOpen((current) => !current)}
                className="flex w-full items-start justify-between gap-3 p-4 text-left"
              >
                <div className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-semibold text-ui-text-primary">
                    <Settings2 className="h-4 w-4 shrink-0" />
                    {t("postProduction.parameters")}
                  </span>
                  {selectedVersion ? (
                    <p className="mt-1 text-xs text-ui-text-muted">
                      {selectedVersion.description || selectedVersion.api_model_code}
                    </p>
                  ) : null}
                </div>
                <ChevronDown
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0 text-ui-text-muted transition-transform",
                    parametersOpen && "rotate-180",
                  )}
                />
              </button>
              {parametersOpen ? (
                <div className="it2tv-scrollbar max-h-[min(30vh,260px)] overflow-y-auto border-t border-ui-border px-4 pt-3 pb-4">
                  <ParametersSchemaForm
                    schema={selectedModel.parameters_schema}
                    value={parameters}
                    onChange={handleParametersChange}
                    errors={parameterErrors}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex shrink-0 flex-col gap-2 border-t border-ui-border p-5 pt-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-ui-text-muted">
                {sourceVersionId ? t("postProduction.sourceVersion", { id: sourceVersionId }) : ""}
              </p>
              <div className="flex items-center gap-2">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost" size="sm">
                    {t("common.cancel")}
                  </Button>
                </Dialog.Close>
                <Button
                  type="button"
                  size="sm"
                  disabled={
                    !sourceVersionId || !selectedVersionId || submitting || loading || selectedModel?.platform_disabled
                  }
                  onClick={() => void handleSubmit()}
                >
                  {submitting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <PostProductionIcon className="h-3.5 w-3.5" />
                  )}
                  {t("postProduction.start")}
                </Button>
              </div>
            </div>
            {submitError ? <p className="text-right text-xs text-ui-status-failed">{submitError}</p> : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
